// Modules

import type {
  RirekishoAddress,
  RirekishoBasicInfo,
  RirekishoContact,
  RirekishoHistoryItem,
  RirekishoLicense,
  RirekishoMotivation,
  RirekishoOptions,
  RirekishoPersonalRequests,
  RirekishoPhoto,
} from '@src/api/core/api-types';
import { getColorModeFromStorage, getFocusModeFromStorage } from '@src/modules/local_storage_utils';
import store from '@src/store';
import { addBizSkill } from '@src/store/slices/biz_skills';
import { updateCareerSummary } from '@src/store/slices/career_summary';
import {
  updateBirthday,
  updateEmail,
  updateName,
  updateNameFurigana,
  updateTelephone,
} from '@src/store/slices/contact/resume';
import { updateRirekishoContact } from '@src/store/slices/contact/rirekisho';
import { updateEducationalBackground } from '@src/store/slices/educational_background';
import { updateColorMode, updateFocusMode } from '@src/store/slices/focus_mode';
import { addHistory } from '@src/store/slices/history/resume';
import { addRirekishoHistoryItem } from '@src/store/slices/history/rirekisho';
import { updateNextCareer } from '@src/store/slices/next_career';
import { addPrivateActivity } from '@src/store/slices/private_activity';
import { addProject } from '@src/store/slices/project';
import { updateRirekishoAddress } from '@src/store/slices/rirekisho/address';
import { updateRirekishoBasicInfo } from '@src/store/slices/rirekisho/basic_info';
import { addRirekishoLicenseItem } from '@src/store/slices/rirekisho/license';
import { updateRirekishoMotivation } from '@src/store/slices/rirekisho/motivation';
import { updateRirekishoOptions } from '@src/store/slices/rirekisho/options';
import { updateRirekishoPersonalRequests } from '@src/store/slices/rirekisho/personal_requests';
import { updateRirekishoPhoto } from '@src/store/slices/rirekisho/photo';
import { addSkill } from '@src/store/slices/skill';
import { updateSkillComment } from '@src/store/slices/skill_comment';
import { updateSkillType } from '@src/store/slices/skill_type';
import type { DateParams } from '@src/types/shared/date_params';
import type { EducationalBackground } from '@src/types/shared/educational_background';
import type { History } from '@src/types/shared/history';
import type { OtherActivity } from '@src/types/shared/other_activity';
import type { Project } from '@src/types/shared/project';
import type { Skill } from '@src/types/shared/skill';
import { format } from 'date-fns';
import { nanoid } from 'nanoid';

export default function setExampleData() {
  const birthday: DateParams = {
    year: '1990',
    month: '4',
    day: '15',
  };
  const histories: History[] = [
    {
      id: nanoid(),
      type: 'officeWorker',
      startDate: {
        year: '2020',
        month: '4',
      },
      endDate: {
        year: '2023',
        month: '3',
      },
      isCurrentJob: false,
      companyName: '株式会社サンプル',
      url: 'https://sample.example.com',
      occupation: 'ソフトウェアエンジニア',
      comment:
        'Webアプリケーション開発に従事。React/TypeScriptを使用したフロントエンド開発とNode.jsを使用したバックエンド開発を担当。',
    },
    {
      id: nanoid(),
      type: 'officeWorker',
      startDate: {
        year: '2023',
        month: '4',
      },
      isCurrentJob: true,
      companyName: '株式会社テクノロジー',
      url: 'https://technology.example.com',
      occupation: 'シニアエンジニア',
      comment:
        'チームリーダーとして開発チームを統括。新規サービスの設計・開発を担当し、技術選定から実装まで幅広く携わっています。',
    },
  ];

  const skills: Skill[] = [
    {
      name: 'JavaScript',
      span: 5,
      isFreeForm: false,
    },
    {
      name: 'TypeScript',
      span: 3,
      isFreeForm: false,
    },
    {
      name: 'React',
      span: 4,
      isFreeForm: false,
    },
    {
      name: 'Node.js',
      span: 3,
      isFreeForm: false,
    },
    {
      name: 'Python',
      span: 2,
      isFreeForm: false,
    },
  ];

  const projects: Project[] = [
    {
      id: nanoid(),
      historyIndex: 0,
      title: 'ECサイト構築プロジェクト',
      position: 'フロントエンドエンジニア',
      capacity: '5名',
      startDate: {
        year: '2021',
        month: '1',
      },
      endDate: {
        year: '2021',
        month: '12',
      },
      isCurrentWork: false,
      summary:
        'React/TypeScriptを使用したECサイトのフロントエンド開発を担当。レスポンシブデザインの実装とパフォーマンス最適化を行いました。',
      url: 'https://ec-example.com',
      techStack: 'React, TypeScript, Node.js, PostgreSQL',
    },
    {
      id: nanoid(),
      historyIndex: 1,
      title: '社内管理システム刷新',
      position: 'テックリード',
      capacity: '8名',
      startDate: {
        year: '2023',
        month: '6',
      },
      endDate: {
        year: '2024',
        month: '3',
      },
      isCurrentWork: false,
      summary:
        'レガシーシステムのモダン化プロジェクト。マイクロサービス化とクラウド移行を主導しました。',
      url: 'https://internal-system.example.com',
      techStack: 'TypeScript, Express.js, Docker, AWS',
    },
  ];

  const skillsComment =
    '技術スキルに関するコメントです。実務ではTypeScriptとReactを中心に開発を行っています。';

  const otherActivities: OtherActivity[] = [
    {
      id: nanoid(),
      url: 'https://github.com/tanaka-taro',
      title: 'OSS活動',
      description:
        'オープンソースプロジェクトに積極的に貢献しています。React関連のライブラリのメンテナンスや、新機能の提案・実装を行っています。',
    },
    {
      id: nanoid(),
      url: 'https://tech-blog.tanaka.example',
      title: '技術ブログ運営',
      description:
        '週1回のペースで技術ブログを更新しています。フロントエンド技術の最新動向や実装のTipsを共有しています。',
    },
    {
      id: nanoid(),
      url: 'https://conference.example.com/speakers/tanaka',
      title: '技術カンファレンス登壇',
      description:
        '年2-3回、技術カンファレンスで登壇しています。React/TypeScriptの実践的な活用方法について発表しています。',
    },
  ];
  const summary =
    'ソフトウェア開発者として4年の経験があります。フロントエンド開発を得意としており、特にReact/TypeScriptを使用したモダンなWebアプリケーション開発に精通しています。最近はチームマネジメントにも携わり、技術選定や設計レビューを通じてチーム全体の生産性向上に貢献しています。';
  const nextCareer =
    'フルスタック開発者として更なるスキルアップを目指し、バックエンド技術やインフラ技術にも精通したいと考えています。また、チームリーダーとしての経験を活かし、より大規模なプロジェクトでテックリードとして活躍したいと思っています。';
  const educationalBackGround: EducationalBackground = {
    graduationDate: {
      year: '2020',
      month: '3',
    },
    institution: '○○大学 情報学部',
    major: '情報科学専攻',
    finishedType: 'completion',
    research: {
      title: 'Webアプリケーションのパフォーマンス最適化に関する研究',
      description:
        'フロントエンド技術におけるレンダリング最適化とバンドルサイズ削減に関する研究を行いました。特にReactアプリケーションにおけるコードスプリッティングとレイジーローディングの効果について分析しました。',
    },
  };

  store.dispatch(updateName('田中太郎'));
  store.dispatch(updateNameFurigana('タナカ タロウ'));
  store.dispatch(updateTelephone('090-1234-5678'));
  store.dispatch(updateEmail('tanaka@example.com'));
  store.dispatch(updateBirthday(birthday));
  store.dispatch(updateCareerSummary(summary));
  store.dispatch(updateSkillComment(skillsComment));
  store.dispatch(updateSkillType('creator'));
  store.dispatch(addBizSkill('プロジェクト管理'));
  store.dispatch(addBizSkill('チームリーダー'));
  store.dispatch(addBizSkill('要件定義'));
  store.dispatch(addBizSkill('システム設計'));
  store.dispatch(updateNextCareer(nextCareer));
  for (const activity of otherActivities) {
    store.dispatch(addPrivateActivity(activity));
  }
  // このアップデートの仕方だ
  store.dispatch(updateEducationalBackground(educationalBackGround));

  for (const item of histories) {
    store.dispatch(addHistory(item));
  }
  for (const item of skills) {
    store.dispatch(addSkill(item));
  }
  for (const item of projects) {
    store.dispatch(addProject(item));
  }

  // フォーカスモードの状態をローカルストレージから復元
  const savedFocusMode = getFocusModeFromStorage();
  store.dispatch(updateFocusMode(savedFocusMode));
  const savedColorMode = getColorModeFromStorage();
  if (savedColorMode) {
    store.dispatch(updateColorMode(savedColorMode));
  }

  // 履歴書サンプルデータの設定
  const rirekishoBasicInfo: RirekishoBasicInfo = {
    lastName: '田中',
    firstName: '太郎',
    lastNameReading: 'たなか',
    firstNameReading: 'たろう',
    gender: 'male',
    birthDate: '1990-04-15',
    currentDate: format(new Date(), 'yyyy-MM-dd'),
    age: 34,
  };

  const rirekishoPhoto: RirekishoPhoto = {
    photoPath: '',
  };

  const rirekishoAddress: RirekishoAddress = {
    zipCode: '150-0002',
    address: '東京都渋谷区渋谷1-2-3 サンプルマンション101',
    addressReading: 'とうきょうとしぶやくしぶや',
    areaCode: '03',
    phoneNumber: '1234-5678',
    callInfo: '平日9:00-18:00',
  };

  const rirekishoContact: RirekishoContact = {
    zipCode: '150-0002',
    contact: '東京都渋谷区渋谷1-2-3 サンプルマンション101',
    contactReading: 'とうきょうとしぶやくしぶや',
    areaCode: '03',
    phoneNumber: '1234-5678',
    callInfo: '平日9:00-18:00',
  };

  const rirekishoHistoryItems: RirekishoHistoryItem[] = [
    {
      year: '2016',
      month: '4',
      content: '○○大学 情報学部 情報科学科 入学',
      type: 'education',
    },
    {
      year: '2020',
      month: '3',
      content: '○○大学 情報学部 情報科学科 卒業',
      type: 'education',
    },
    {
      year: '2020',
      month: '4',
      content: '株式会社サンプル 入社（システム開発部配属）',
      type: 'work',
    },
    {
      year: '2023',
      month: '3',
      content: '株式会社サンプル 退社',
      type: 'work',
    },
    {
      year: '2023',
      month: '4',
      content: '株式会社テクノロジー 入社（開発部配属）',
      type: 'work',
    },
  ];

  const rirekishoLicenses: RirekishoLicense[] = [
    {
      year: '2018',
      month: '10',
      name: '基本情報技術者試験 合格',
    },
    {
      year: '2020',
      month: '6',
      name: '応用情報技術者試験 合格',
    },
    {
      year: '2022',
      month: '3',
      name: 'AWS Solutions Architect Associate 取得',
    },
  ];

  const rirekishoMotivation: RirekishoMotivation = {
    body: `【志望動機】
技術を通じて社会に貢献したいという強い思いから、ソフトウェア開発者として成長し続けたいと考えております。特にユーザーに価値を提供できるプロダクト開発に携わりたいです。

【特技】
Web技術全般、特にフロントエンド開発に精通しており、React/TypeScriptを用いたモダンなWebアプリケーション開発が得意です。

【好きな学科】
大学時代はアルゴリズムとデータ構造、ソフトウェア工学を専攻し、効率的なプログラム設計について学びました。

【アピールポイント】
チームワークを大切にし、技術的な課題解決能力と継続的な学習意欲を持っています。新しい技術の習得にも積極的に取り組みます。`,
  };

  const rirekishoPersonalRequests: RirekishoPersonalRequests = {
    requests: ['リモートワーク可能', '土日祝日休み', '残業時間月20時間以内'],
    additionalNote: '勤務地は都内近郊を希望いたします。',
  };

  const rirekishoOptions: RirekishoOptions = {
    insertBlankLine: true,
    showGender: true,
    showPhoto: true,
  };

  // 履歴書データをstoreに設定
  store.dispatch(updateRirekishoBasicInfo(rirekishoBasicInfo));
  store.dispatch(updateRirekishoPhoto(rirekishoPhoto));
  store.dispatch(updateRirekishoAddress(rirekishoAddress));
  store.dispatch(updateRirekishoContact(rirekishoContact));
  store.dispatch(updateRirekishoMotivation(rirekishoMotivation));
  store.dispatch(updateRirekishoPersonalRequests(rirekishoPersonalRequests));
  store.dispatch(updateRirekishoOptions(rirekishoOptions));

  // 学歴・職歴の追加
  for (const item of rirekishoHistoryItems) {
    store.dispatch(addRirekishoHistoryItem(item));
  }

  // 免許・資格の追加
  for (const item of rirekishoLicenses) {
    store.dispatch(addRirekishoLicenseItem(item));
  }
}
